// vectorStore.ts
import { MemoryVectorStore } from "langchain/vectorstores/memory";
import { Document } from "@langchain/core/documents";
import { EnhancedOpenAIEmbeddings } from "./openaiEmbeddings"; // <-- Use OpenAI embeddings instead
import { processPdfFile, processTextContent, extractedImages } from "./documentProcessor"; // your text/PDF processing
import axios from 'axios';
import { createClient } from '@supabase/supabase-js';

// Single instance of embeddings to reuse
let embeddingsInstance: EnhancedOpenAIEmbeddings | null = null;
// Vector store instance
let vectorStore: MemoryVectorStore | null = null;
// Track if the knowledge base is loaded
let isKnowledgeBaseLoaded = false;
// Cache for queries to improve response time
const queryCache: Map<string, string> = new Map();
const CACHE_SIZE = 30;
// Default knowledge base path
const KNOWLEDGE_BASE_URL = process.env.PUBLIC_URL + '/knowledge_base.txt';

// Get Supabase credentials from environment with hardcoded fallback
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://fgovywiapnpjgqoviicz.supabase.co';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZnb3Z5d2lhcG5wamdxb3ZpaWN6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDI3OTI4MjgsImV4cCI6MjA1ODM2ODgyOH0.uRxpNAgcjLQGUL1Oq23QfFniPMHtfLX_6cIha4DHUa0';

// Lazy-initialize Supabase client
let supabase: ReturnType<typeof createClient> | null = null;

function getSupabase() {
  if (!supabase) {
    supabase = createClient(SUPABASE_URL, SUPABASE_KEY);
  }
  return supabase;
}

/**
 * Gets or initializes the embeddings model
 */
async function getEmbeddings(): Promise<EnhancedOpenAIEmbeddings> {
  if (!embeddingsInstance) {
    console.log('Initializing OpenAI embeddings model...');
    embeddingsInstance = new EnhancedOpenAIEmbeddings();
  }
  return embeddingsInstance;
}

/**
 * Initializes the vector store with a default knowledge base
 */
export async function initializeKnowledgeBase(): Promise<boolean> {
  try {
    console.log('Initializing knowledge base...');
    
    // Get or initialize embeddings
    const embeddings = await getEmbeddings();
    
    // Initialize empty vector store
    vectorStore = new MemoryVectorStore(embeddings);
    
    // Load default knowledge base
    try {
      const response = await axios.get(KNOWLEDGE_BASE_URL);
      const text = response.data;
      console.log(`Loaded knowledge base: ${text.length} characters`);
      
      // Convert text into Documents
      const documents = await processTextContent(text);
      
      // Add them to vector store
      await vectorStore.addDocuments(documents);
      
      isKnowledgeBaseLoaded = true;
      console.log('Knowledge base initialized successfully');
      return true;
    } catch (error) {
      console.error('Error loading knowledge base file:', error);
      
      // fallback if not found
      const fallbackText = `Some fallback content here...`;
      const documents = await processTextContent(fallbackText, 'fallback_knowledge_base');
      await vectorStore.addDocuments(documents);
      
      isKnowledgeBaseLoaded = true;
      console.log('Fallback knowledge base initialized');
      return true;
    }
  } catch (error) {
    console.error('Error initializing knowledge base:', error);
    return false;
  }
}

/**
 * Adds a document to the vector store
 */
async function addDocumentToVectorStore(content: string): Promise<void> {
  console.log('Adding content to vector store...');
  try {
    // Initialize vector store if not already done
    if (!vectorStore) {
      const embeddings = await getEmbeddings();
      vectorStore = new MemoryVectorStore(embeddings);
    }

    // Process the document content
    const docs = await processTextContent(content);
    
    // Add the processed documents to the vector store
    await vectorStore.addDocuments(docs);
    console.log(`Added ${docs.length} chunks to vector store`);
  } catch (error) {
    console.error('Error adding document to vector store:', error);
    throw error;
  }
}

/**
 * Processes and uploads a PDF to the vector store
 */
export async function uploadPDF(file: File): Promise<boolean> {
  try {
    console.log('Processing uploaded PDF...');
    const { text, imageDescriptions } = await processPdfFile(file);
    
    // Combine text with image descriptions
    let combinedContent = text;
    
    if (imageDescriptions.length > 0) {
      combinedContent += "\n\n## Visual Elements in Document\n\n";
      combinedContent += imageDescriptions.join("\n\n");
    }
    
    // Add to vector store
    await addDocumentToVectorStore(combinedContent);
    console.log('PDF processed and added to vector store');
    return true;
  } catch (error) {
    console.error('Error processing PDF:', error);
    return false;
  }
}

/**
 * Searches the vector store for relevant docs
 */
export async function searchDocuments(query: string, topK: number = 5): Promise<any[]> {
  try {
    if (!isKnowledgeBaseLoaded || !vectorStore) {
      const success = await loadPreprocessedData(); // Use loadPreprocessedData instead of initializeKnowledgeBase
      if (!success || !vectorStore) {
        return [];
      }
    }
    
    // Check cache - but return cached results instead of empty array
    const cacheKey = query.toLowerCase().trim();
    if (queryCache.has(cacheKey)) {
      console.log(`Using cached results for "${query}"`);
      const cachedResult = queryCache.get(cacheKey);
      return Array.isArray(cachedResult) ? cachedResult : [];
    }
    
    console.log(`Searching for "${query}" with topK=${topK}`);
    
    // Search vector store
    const results = await vectorStore.similaritySearch(query, topK);
    // Store in cache
    queryCache.set(cacheKey, JSON.stringify(results));
    if (queryCache.size > CACHE_SIZE) {
      // Remove oldest entry - ensure it's not undefined  
      const keysIterator = queryCache.keys();
      const firstKey = keysIterator.next().value;
      if (firstKey) {
        queryCache.delete(firstKey);
      }
    }
    
    console.log(`Found ${results.length} relevant documents for "${query}"`);
    if (results.length > 0) {
      console.log("First result preview:", results[0].pageContent?.substring(0, 100) || "No content available");
    }
    
    return results;
  } catch (error) {
    console.error("Error in searchDocuments:", error);
    return [];
  }
}

/**
 * Loads pre-processed data from Supabase
 */
export async function loadPreprocessedData(): Promise<boolean> {
  try {
    console.log('Loading pre-processed data from Supabase...');
    
    const embeddings = await getEmbeddings();
    const supabaseClient = getSupabase();
    
    // 1. Load chunks and their embeddings
    const { data: chunks, error: chunksError } = await supabaseClient
      .from('chunks')
      .select('*');
      
    if (chunksError) {
      console.error('Error loading chunks from Supabase:', chunksError);
      return initializeKnowledgeBase(); // Fall back to default if error
    }
    
    if (!chunks || chunks.length === 0) {
      console.log('No chunks found in Supabase, falling back to default initialization');
      return initializeKnowledgeBase();
    }
    
    console.log(`Found ${chunks.length} chunks in Supabase`);
    
    // 2. Initialize vector store properly without using 'vectors' parameter
    vectorStore = new MemoryVectorStore(embeddings);
    
    // Convert chunks to Documents
    const documents = chunks.map((chunk: any) => {
      return new Document({
        pageContent: chunk.content as string,
        metadata: typeof chunk.metadata === 'string' 
          ? JSON.parse(chunk.metadata || '{}') 
          : (chunk.metadata || {})
      });
    });
    
    // Add documents to the vector store
    // Note: This will re-compute embeddings, but we'll optimize this later
    await vectorStore.addDocuments(documents);
    
    // 3. Load images (which is already working)
    const { data: images, error: imagesError } = await supabaseClient
      .from('images')
      .select('*');
      
    if (imagesError) {
      console.error('Error loading images from Supabase:', imagesError);
    } else if (images && images.length > 0) {
      console.log(`Loaded ${images.length} images from Supabase`);
      
      // Clear existing images first
      extractedImages.clear();
      
      // Add all images from Supabase
      for (const image of images) {
        const imgData = image as any;
        extractedImages.set(imgData.image_id, {
          dataUrl: imgData.imgbb_url,
          pageNumber: imgData.page_number,
          description: imgData.description
        });
      }
    } else {
      console.log('No images found in Supabase');
    }
    
    isKnowledgeBaseLoaded = true;
    console.log('Pre-processed data loaded successfully from Supabase');
    return true;
  } catch (error) {
    console.error('Error loading pre-processed data from Supabase:', error);
    // Fall back to regular initialization
    return initializeKnowledgeBase();
  }
}
